import { useCallback, useEffect, useLayoutEffect, useState } from "react";
import { X } from "lucide-react";
import api from "../../lib/api";

const STEPS = [
  { target: "nav-sidebar", title: "Everything lives in this menu", body: "Pages are grouped by what you're doing: Members, Sales & Marketing, Money, Building Ops and Admin. The grey line under each page tells you what it's for." },
  { target: "nav-location-select", needsLocations: true, title: "Pick a location first", body: "Choose a location here and pages with a map pin open filtered to it. Set it back to \u201cAll locations\u201d to see everything." },
  { target: "nav-cmd-k-trigger", title: "Find any page fast", body: "Click here or press Ctrl K (\u2318 K on Mac), type a few letters like \u201ccrm\u201d or a location name, and press Enter." },
  { target: "nav-item-home", title: "Pin your favourites", body: "Hover any page in the menu and click the \u2605 to pin it. Pinned pages appear at the top of the menu and on your Home page." },
];
const PAD = 8;

function useRect(testid, step) {
  const [rect, setRect] = useState(null);
  useLayoutEffect(() => {
    const measure = () => {
      const el = document.querySelector(`aside [data-testid="${testid}"]`);
      const r = el?.getBoundingClientRect();
      setRect(r && r.width ? r : null);
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, [testid, step]);
  return rect;
}

export function OnboardingTour({ user, hasLocations }) {
  const [open, setOpen] = useState(false);
  const [i, setI] = useState(0);
  const steps = STEPS.filter((s) => !s.needsLocations || hasLocations);
  const step = steps[i] || steps[0];
  const rect = useRect(step.target, i);

  useEffect(() => {
    if (user && user.tour_done === false) { const t = setTimeout(() => setOpen(true), 600); return () => clearTimeout(t); }
  }, [user]);
  useEffect(() => {
    const start = () => { setI(0); setOpen(true); };
    window.addEventListener("start-tour", start);
    return () => window.removeEventListener("start-tour", start);
  }, []);

  const finish = useCallback(() => {
    setOpen(false);
    if (user && user.tour_done === false) { user.tour_done = true; api.post("/auth/tour-done").catch(() => {}); }
  }, [user]);
  if (!open) return null;

  const last = i === steps.length - 1;
  const cardStyle = rect
    ? { top: Math.min(Math.max(rect.top, 16), window.innerHeight - 260), left: rect.right + 20 }
    : { top: "50%", left: "50%", transform: "translate(-50%, -50%)" };
  return (
    <div className="fixed inset-0 z-[60]" data-testid="tour-overlay">
      {rect
        ? <div className="absolute rounded-xl pointer-events-none transition-all duration-300 ring-2 ring-brand-gold"
            style={{ top: rect.top - PAD, left: rect.left - PAD, width: rect.width + PAD * 2, height: rect.height + PAD * 2, boxShadow: "0 0 0 9999px rgba(20,18,16,0.55)" }} />
        : <div className="absolute inset-0 bg-black/55" />}
      <div className="absolute w-[min(340px,calc(100vw-32px))] bg-brand-page border border-brand-border rounded-xl shadow-2xl p-5 transition-all duration-300" style={cardStyle} data-testid="tour-card">
        <button onClick={finish} aria-label="Close tour" data-testid="tour-close" className="absolute right-3 top-3 p-1 text-brand-tertiary hover:text-brand-ink"><X className="h-4 w-4" /></button>
        <p className="text-[10px] uppercase tracking-[0.15em] text-brand-tertiary" data-testid="tour-step-count">Step {i + 1} of {steps.length}</p>
        <h3 className="font-serif text-2xl font-light text-brand-ink mt-1 pr-6" data-testid="tour-title">{step.title}</h3>
        <p className="text-sm text-brand-muted mt-2">{step.body}</p>
        {!rect && <p className="text-xs text-brand-tertiary mt-2">On a phone, tap ☰ at the top left to open the menu.</p>}
        <div className="flex items-center gap-2 mt-5">
          <div className="flex gap-1 mr-auto">{steps.map((_, k) => <span key={k} className={`h-1.5 rounded-full transition-all ${k === i ? "w-5 bg-brand-ink" : "w-1.5 bg-brand-border"}`} />)}</div>
          <button onClick={finish} data-testid="tour-skip" className="text-xs text-brand-muted hover:text-brand-ink px-2 py-1.5 transition-colors">Skip</button>
          {i > 0 && <button onClick={() => setI(i - 1)} data-testid="tour-back" className="text-xs border border-brand-border rounded-full px-3 py-1.5 text-brand-ink hover:bg-brand-hover transition-colors">Back</button>}
          <button onClick={() => (last ? finish() : setI(i + 1))} data-testid="tour-next" className="text-xs bg-brand-ink text-brand-page rounded-full px-4 py-1.5 hover:opacity-90 transition-opacity">{last ? "Got it" : "Next"}</button>
        </div>
      </div>
    </div>
  );
}
